import { useQuery, QueryKey, useIsFetching } from '@tanstack/react-query';
import { parseResponseDateAndTime } from 'src/common/utils/parseResponseDate';
import { SimData } from 'src/common/components/Sim/constants/sim';
import { fetchSessionsListDetails } from '../SimInventoryApi/simInventoryApi';
import { useHasFeatureFlag } from 'src/featureFlags/useHasFeatureFlag';

type SessionListDetails = {
  currentIpAddress: string;
  sessionStatusLastUpdated: string;
};

type SessionsDetailsListMap = Record<string, SessionListDetails>;

export const SESSION_LIST_DETAILS_QUERY_KEY = 'SessionListDetails';

export const enrichWithSessionListDetailsMapper = (
  originalData: SimData,
  sessionsDetails?: SessionsDetailsListMap,
): SimData => {
  const imsi = originalData.imsi;
  const session = imsi ? sessionsDetails?.[imsi] : undefined;

  return {
    ...originalData,
    currentIpAddress: session?.currentIpAddress,
    sessionStatusLastUpdated: parseResponseDateAndTime(session?.sessionStatusLastUpdated),
  };
};

type UseSessionsDetailsListProps = {
  imsis: string[];
  queryKey: QueryKey;
};

export const useSessionsListDetails = ({ imsis, queryKey }: UseSessionsDetailsListProps) => {
  const isRadiusInterimsFilteringEnabled = useHasFeatureFlag('RadiusInterimsFiltering');

  return useQuery({
    queryKey,
    queryFn: async () => {
      const data = await fetchSessionsListDetails(imsis);
      return data;
    },
    select: (data) => {
      const details = data.reduce<SessionsDetailsListMap>((acc, session) => {
        acc[session.imsi] = {
          currentIpAddress: session.currentIpAddress,
          sessionStatusLastUpdated: session.lastUpdated,
        };
        return acc;
      }, {});

      return details;
    },
    enabled: !!imsis?.length && isRadiusInterimsFilteringEnabled,
  });
};

export const useIsSessionDetailsListDetailsLoading = () =>
  useIsFetching({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey.includes(SESSION_LIST_DETAILS_QUERY_KEY),
  }) > 0;
